import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { type DateRange, DayPicker, type DayPickerProps, type Matcher } from "react-day-picker";
import { cx } from "../internal/cx.js";

export type { DateRange, DayPickerProps, Matcher };

/** How many years are shown per page in the year-selection panel. */
const YEARS_PER_PAGE = 12;

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_SHORT = MONTH_LABELS.map((m) => m.slice(0, 3));

type CalendarView = "days" | "months" | "years";

export type CalendarProps = DayPickerProps & {
  className?: string;
  /**
   * Earliest selectable year for the month/year quick-jump panels. Defaults to
   * 1920 so date-of-birth fields can reach back a century without scrolling.
   * Derived from `fromDate`/`fromMonth`/`fromYear` when those are supplied.
   */
  fromYear?: number;
  /**
   * Latest selectable year for the month/year quick-jump panels. Defaults to
   * the current year + 10. Derived from `toDate`/`toMonth`/`toYear` when those
   * are supplied.
   */
  toYear?: number;
  /**
   * Which panel the calendar opens on. Defaults to `"days"`. Set to `"months"`
   * to use the calendar as a month picker (pair with `onMonthSelect`); the day
   * grid is never shown in that mode.
   */
  initialView?: CalendarView;
  /**
   * Fires when a month cell is chosen in the month panel with the first of that
   * month. When provided, picking a month EMITS it (rather than drilling into
   * the day grid) — this is what turns the month panel into a month picker.
   */
  onMonthSelect?: (date: Date) => void;
};

function yearFrom(
  date: Date | undefined,
  month: Date | undefined,
  year: number | undefined,
): number | undefined {
  if (typeof year === "number") return year;
  if (month) return month.getFullYear();
  if (date) return date.getFullYear();
  return undefined;
}

/**
 * Calendar — month-grid date selection built on `react-day-picker` v8, with
 * Ant-Design-style quick navigation: clicking the month or year in the header
 * swaps the day grid for a month picker or a paged year (decade) picker, so
 * jumping to a date-of-birth or hire date is two clicks instead of dozens of
 * month-arrow taps.
 *
 * Use stand-alone for inline date selection, or compose inside a Popover
 * (`DatePicker`) for compact form fields. Modes: `single`, `range`,
 * `multiple` — the underlying DayPicker prop.
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fromYear,
  toYear,
  fromDate,
  fromMonth,
  toDate,
  toMonth,
  month,
  defaultMonth,
  onMonthChange,
  initialView = "days",
  onMonthSelect,
  ...props
}: CalendarProps) {
  const today = React.useMemo(() => new Date(), []);

  const minYear = yearFrom(fromDate, fromMonth, fromYear) ?? 1920;
  const maxYear = yearFrom(toDate, toMonth, toYear) ?? today.getFullYear() + 10;

  // The displayed month is controlled here so the header panels can drive it
  // (clicking a month/year navigates the grid) while still honoring a
  // consumer-controlled `month` prop when one is passed.
  const isMonthControlled = month !== undefined;
  const [internalMonth, setInternalMonth] = React.useState<Date>(
    () => month ?? defaultMonth ?? today,
  );
  const displayMonth = isMonthControlled ? (month as Date) : internalMonth;

  const goToMonth = React.useCallback(
    (next: Date) => {
      if (!isMonthControlled) setInternalMonth(next);
      onMonthChange?.(next);
    },
    [isMonthControlled, onMonthChange],
  );

  const [view, setView] = React.useState<CalendarView>(initialView);
  // First year shown in the year panel's current page.
  const [yearPageStart, setYearPageStart] = React.useState<number>(() => {
    const base = displayMonth.getFullYear();
    return base - ((((base - minYear) % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE);
  });

  const headerLabel =
    view === "years"
      ? `${yearPageStart} – ${yearPageStart + YEARS_PER_PAGE - 1}`
      : `${displayMonth.getFullYear()}`;

  const openYears = () => {
    const base = displayMonth.getFullYear();
    setYearPageStart(base - ((base - minYear) % YEARS_PER_PAGE));
    setView("years");
  };

  const stepYearPage = (direction: 1 | -1) => {
    setYearPageStart((prev) => prev + direction * YEARS_PER_PAGE);
  };

  const selectMonth = (monthIndex: number) => {
    const picked = new Date(displayMonth.getFullYear(), monthIndex, 1);
    goToMonth(picked);
    // Month-picker mode: emit the chosen month and stay on the month grid
    // (don't drill into days). Otherwise, month-click is just quick-nav.
    if (onMonthSelect) {
      onMonthSelect(picked);
      return;
    }
    setView("days");
  };

  const selectYear = (year: number) => {
    goToMonth(new Date(year, displayMonth.getMonth(), 1));
    setView("months");
  };

  // ---------- Header (shared across all three views) ----------
  const header = (
    <div className="cw-v2-calendar__caption">
      <div className="cw-v2-calendar__nav cw-v2-calendar__nav--start">
        <button
          type="button"
          className="cw-v2-calendar__nav-button"
          aria-label={
            view === "years"
              ? "Previous years"
              : view === "months"
                ? "Previous year"
                : "Previous month"
          }
          onClick={() => {
            if (view === "years") stepYearPage(-1);
            else if (view === "months")
              goToMonth(new Date(displayMonth.getFullYear() - 1, displayMonth.getMonth(), 1));
            else goToMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1));
          }}
        >
          <ChevronLeft className="cw-v2-calendar__nav-icon" aria-hidden />
        </button>
      </div>

      <div className="cw-v2-calendar__caption-labels" aria-live="polite">
        {view === "days" ? (
          <>
            <button
              type="button"
              className="cw-v2-calendar__caption-label cw-v2-calendar__caption-label--button"
              onClick={() => setView("months")}
            >
              {MONTH_LABELS[displayMonth.getMonth()]}
            </button>
            <button
              type="button"
              className="cw-v2-calendar__caption-label cw-v2-calendar__caption-label--button"
              onClick={openYears}
            >
              {displayMonth.getFullYear()}
            </button>
          </>
        ) : view === "months" ? (
          <button
            type="button"
            className="cw-v2-calendar__caption-label cw-v2-calendar__caption-label--button"
            onClick={openYears}
          >
            {headerLabel}
          </button>
        ) : (
          <span className="cw-v2-calendar__caption-label">{headerLabel}</span>
        )}
      </div>

      <div className="cw-v2-calendar__nav cw-v2-calendar__nav--end">
        <button
          type="button"
          className="cw-v2-calendar__nav-button"
          aria-label={
            view === "years" ? "Next years" : view === "months" ? "Next year" : "Next month"
          }
          onClick={() => {
            if (view === "years") stepYearPage(1);
            else if (view === "months")
              goToMonth(new Date(displayMonth.getFullYear() + 1, displayMonth.getMonth(), 1));
            else goToMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1));
          }}
        >
          <ChevronRight className="cw-v2-calendar__nav-icon" aria-hidden />
        </button>
      </div>
    </div>
  );

  if (view === "months") {
    return (
      <div className={cx("cw-v2-calendar", className)}>
        {header}
        <fieldset className="cw-v2-calendar__panel-grid">
          <legend className="cw-v2-calendar__panel-legend">Select month</legend>
          {MONTH_SHORT.map((label, index) => {
            // In month-picker mode, highlight the chosen month/year (from the
            // `selected` Date). Otherwise fall back to the displayed month.
            const selectedDate =
              onMonthSelect && props.selected instanceof Date ? props.selected : undefined;
            const selected = selectedDate
              ? selectedDate.getFullYear() === displayMonth.getFullYear() &&
                selectedDate.getMonth() === index
              : index === displayMonth.getMonth();
            return (
              <button
                key={label}
                type="button"
                aria-pressed={selected}
                className={cx(
                  "cw-v2-calendar__panel-cell",
                  selected && "cw-v2-calendar__panel-cell--selected",
                )}
                onClick={() => selectMonth(index)}
              >
                {label}
              </button>
            );
          })}
        </fieldset>
      </div>
    );
  }

  if (view === "years") {
    const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i);
    return (
      <div className={cx("cw-v2-calendar", className)}>
        {header}
        <fieldset className="cw-v2-calendar__panel-grid">
          <legend className="cw-v2-calendar__panel-legend">Select year</legend>
          {years.map((year) => {
            const selected = year === displayMonth.getFullYear();
            const disabled = year < minYear || year > maxYear;
            return (
              <button
                key={year}
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                className={cx(
                  "cw-v2-calendar__panel-cell",
                  selected && "cw-v2-calendar__panel-cell--selected",
                  disabled && "cw-v2-calendar__panel-cell--disabled",
                )}
                onClick={() => selectYear(year)}
              >
                {year}
              </button>
            );
          })}
        </fieldset>
      </div>
    );
  }

  // ---------- Day view (react-day-picker grid, custom caption) ----------
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      month={displayMonth}
      onMonthChange={goToMonth}
      fromDate={fromDate}
      fromMonth={fromMonth}
      toDate={toDate}
      toMonth={toMonth}
      className={cx("cw-v2-calendar", className)}
      classNames={{
        months: "cw-v2-calendar__months",
        month: "cw-v2-calendar__month",
        caption: "cw-v2-calendar__caption",
        caption_label: "cw-v2-calendar__caption-label",
        nav: "cw-v2-calendar__nav",
        nav_button: "cw-v2-calendar__nav-button",
        nav_button_previous: "cw-v2-calendar__nav-button--previous",
        nav_button_next: "cw-v2-calendar__nav-button--next",
        table: "cw-v2-calendar__table",
        head_row: "cw-v2-calendar__head-row",
        head_cell: "cw-v2-calendar__head-cell",
        row: "cw-v2-calendar__row",
        cell: "cw-v2-calendar__cell",
        day: "cw-v2-calendar__day",
        day_selected: "cw-v2-calendar__day--selected",
        day_today: "cw-v2-calendar__day--today",
        day_outside: "cw-v2-calendar__day--outside",
        day_disabled: "cw-v2-calendar__day--disabled",
        day_range_start: "cw-v2-calendar__day--range-start",
        day_range_end: "cw-v2-calendar__day--range-end",
        day_range_middle: "cw-v2-calendar__day--range-middle",
        day_hidden: "cw-v2-calendar__day--hidden",
        ...classNames,
      }}
      components={{
        Caption: () => header,
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";
